import {
    useEffect,
    useState,
    useRef
} from "react";
import {
    type LayoutChangeEvent,
    Animated
} from "react-native";
import type {
    CollapseConstantType,
    CollapsePhase,
    CollapseType
} from "./type";
import {
    COLLAPSE_HEIGHT_EPSILON
} from "./stylesheet";
import {
    useMotionTiming
} from "../../../../helpers/motion";

const useCollapse = ({
    isKeepMounted,
    customTheme,
    isOpen
}: CollapseConstantType): CollapseType => {
    const expandTiming = useMotionTiming("expand", customTheme);

    const collapseTiming = useMotionTiming("collapse", customTheme);

    const [
        phase,
        setPhase
    ] = useState<CollapsePhase>(isOpen ? "open" : "closed");

    const animationRef = useRef<Animated.CompositeAnimation | null>(null);

    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const height = useRef(new Animated.Value(0)).current;

    const opacity = useRef(new Animated.Value(1)).current;

    const naturalHeightRef = useRef(0);

    const phaseRef = useRef<CollapsePhase>(phase);

    const handleStop = () => {
        animationRef.current?.stop();

        animationRef.current = null;

        if(timerRef.current === null) {
            return;
        }

        clearTimeout(timerRef.current);

        timerRef.current = null;
    };

    const handlePhase = (nextPhase: CollapsePhase) => {
        phaseRef.current = nextPhase;

        setPhase(nextPhase);
    };

    const handleAnimate = (toValue: number, nextPhase: CollapsePhase) => {
        handleStop();

        const timing = nextPhase === "open" ? expandTiming : collapseTiming;

        const getAnimation = (value: Animated.Value, nextValue: number) => Animated.timing(value, {
            duration: timing.duration,
            useNativeDriver: false,
            easing: timing.easing,
            isInteraction: false,
            toValue: nextValue
        });

        const animation = Animated.parallel([
            getAnimation(height, toValue),
            getAnimation(opacity, nextPhase === "open" ? 1 : 0)
        ]);

        animationRef.current = animation;

        animation.start(({
            finished
        }) => {
            if(!finished) {
                return;
            }

            animationRef.current = null;

            handlePhase(nextPhase);
        });
    };

    const onLayout = (event: LayoutChangeEvent) => {
        if(phaseRef.current === "closed") {
            return;
        }

        const nextHeight = event.nativeEvent.layout.height;

        const isChanged = Math.abs(nextHeight - naturalHeightRef.current) > COLLAPSE_HEIGHT_EPSILON;

        naturalHeightRef.current = nextHeight;

        if(phaseRef.current !== "opening") {
            return;
        }

        if(!nextHeight) {
            handleStop();

            handlePhase("open");

            return;
        }

        if(animationRef.current && !isChanged) {
            return;
        }

        handleAnimate(nextHeight, "open");
    };

    useEffect(() => {
        if(isOpen) {
            if(phaseRef.current === "open" || phaseRef.current === "opening") {
                return;
            }

            if(!expandTiming.duration) {
                handleStop();

                handlePhase("open");

                return;
            }

            if(phaseRef.current === "closed") {
                height.setValue(0);

                opacity.setValue(0);
            }

            handlePhase("opening");

            if(naturalHeightRef.current) {
                handleAnimate(naturalHeightRef.current, "open");

                return;
            }

            timerRef.current = setTimeout(() => {
                timerRef.current = null;

                handlePhase("open");
            }, expandTiming.duration);

            return;
        }

        if(phaseRef.current === "closed" || phaseRef.current === "closing") {
            return;
        }

        if(!collapseTiming.duration) {
            handleStop();

            handlePhase("closed");

            return;
        }

        if(phaseRef.current === "open") {
            height.setValue(naturalHeightRef.current);

            opacity.setValue(1);
        }

        handlePhase("closing");

        handleAnimate(0, "closed");
    }, [
        isOpen
    ]);

    useEffect(() => {
        return () => {
            handleStop();
        };
    }, []);

    return {
        isRendered: phase !== "closed" || isKeepMounted,
        onLayout,
        opacity,
        height,
        phase
    };
};
export default useCollapse;
